import React, { useCallback, useEffect, useState } from "react";
import { useMatch } from "react-router-dom";
import { User, PencilSimple } from "@phosphor-icons/react";
import { listCharacters, uploadBaseImage, fileUrl } from "@/lib/api";
import { toast } from "sonner";

function Badge({ character, onUpdated }) {
  const fileInputRef = React.useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const updated = await uploadBaseImage(character.id, file);
      toast.success(`Base reference updated for ${character.name}`);
      onUpdated(updated);
    } catch (err) {
      toast.error("Failed to update base reference image");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="relative group" title={character.name}>
      {character.base_image_path ? (
        <img
          data-testid={`header-main-character-badge-${character.id}`}
          src={fileUrl(character.base_image_path)}
          alt={character.name}
          onClick={() => fileInputRef.current.click()}
          className="w-8 h-8 rounded-full object-cover border border-fuchsia-500/60 cursor-pointer"
        />
      ) : (
        <div
          data-testid={`header-main-character-badge-${character.id}`}
          onClick={() => fileInputRef.current.click()}
          className="w-8 h-8 rounded-full border border-dashed border-amber-500 flex items-center justify-center text-amber-500 cursor-pointer"
        >
          <User size={14} />
        </div>
      )}
      <button
        data-testid={`header-main-character-badge-edit-${character.id}`}
        onClick={() => fileInputRef.current.click()}
        title="Replace base reference image"
        className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-fuchsia-400"
      >
        <PencilSimple size={8} />
      </button>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
    </div>
  );
}

export default function MainCharacterBadges() {
  const matchWorkspace = useMatch("/stories/:storyId");
  const matchManifest = useMatch("/stories/:storyId/manifest");
  const storyId = matchWorkspace?.params?.storyId || matchManifest?.params?.storyId;
  const [characters, setCharacters] = useState([]);

  const load = useCallback(() => {
    if (!storyId) {
      setCharacters([]);
      return;
    }
    listCharacters(storyId)
      .then((data) => setCharacters(data.filter((c) => c.is_main)))
      .catch(() => {});
  }, [storyId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.addEventListener("main-characters-updated", load);
    return () => window.removeEventListener("main-characters-updated", load);
  }, [load]);

  if (!storyId || characters.length === 0) return null;

  const handleUpdated = (updated) => {
    setCharacters((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  return (
    <div data-testid="header-main-character-badges" className="flex items-center gap-2">
      {characters.map((c) => (
        <Badge key={c.id} character={c} onUpdated={handleUpdated} />
      ))}
    </div>
  );
}
